/**
 * CommentTemplateDialog Component
 *
 * Dialog for creating and editing comment templates.
 * Includes form validation, rich text editing, and API integration.
 *
 * Features:
 * - Create mode (template is null) and Edit mode (template provided)
 * - Name input with required validation and 255 character limit
 * - Rich text content editor with required validation and 32,000 character limit
 * - Loading states during form submission
 * - Error handling for validation and API errors
 *
 * @param template - The comment template to edit (null for create mode)
 * @param open - Controls dialog visibility
 * @param onOpenChange - Callback when dialog open state changes
 *
 * @example
 * ```tsx
 * <CommentTemplateDialog
 *   template={editingTemplate}
 *   open={dialogOpen}
 *   onOpenChange={setDialogOpen}
 * />
 * ```
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'
import { Descendant } from 'slate'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  useCreateCommentTemplate,
  useUpdateCommentTemplate,
} from '@/features/comment-template/hooks'
import { RichTextEditor } from '@/features/view-tfl-uploaded/components/RichTextEditor/RichTextEditor'
import { EditorMode } from '@/features/view-tfl-uploaded/constants'
import type { EditorModeType } from '@/features/view-tfl-uploaded/types'
import {
  initialEditorValue,
  parseMarkdownToSlate,
  serializeToMarkdown,
} from '@/features/view-tfl-uploaded/utils/slateUtils'
import { ResponseCommentTemplate } from '@/schema/commentTemplate'

const MAX_NAME_LENGTH = 255
const MAX_CONTENT_LENGTH = 32000

interface CommentTemplateFormData {
  name: string
  content: Descendant[]
}

interface CommentTemplateDialogProps {
  template: ResponseCommentTemplate | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const CommentTemplateDialog: React.FC<CommentTemplateDialogProps> = ({
  template,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation()
  const isEditMode = useMemo(() => !!template, [template])
  const [editorMode, setEditorMode] = useState<EditorModeType>(EditorMode.PLAIN)
  const [isSwitchingMode, setIsSwitchingMode] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors, isValid },
    setValue,
    setError,
  } = useForm<CommentTemplateFormData>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      content: initialEditorValue,
    },
  })

  const createMutation = useCreateCommentTemplate()
  const updateMutation = useUpdateCommentTemplate()

  const handleApiError = (error: unknown) => {
    const axiosError = error as AxiosError<{ error?: { message?: string } }>
    const errorMessage =
      axiosError.response?.data?.error?.message ||
      t('commentTemplate.dialog.messages.errorGeneric')

    setError('name', {
      type: 'manual',
      message: errorMessage,
    })
  }

  useEffect(() => {
    if (isEditMode && template && open) {
      setValue('name', template.name)
      setValue('content', parseMarkdownToSlate(template.content))
    } else if (open) {
      setValue('name', '')
      setValue('content', initialEditorValue)
    }
  }, [isEditMode, template, setValue, open])

  useEffect(() => {
    if (!open) {
      reset({
        name: '',
        content: initialEditorValue,
      })
      setEditorMode(EditorMode.PLAIN)
    }
  }, [open, reset])

  const onSubmit = (data: CommentTemplateFormData) => {
    const contentMarkdown = serializeToMarkdown(data.content)

    const formData = {
      name: data.name.trim(),
      content: contentMarkdown,
    }

    if (isEditMode && template) {
      updateMutation.mutate(
        {
          id: template.id,
          data: formData,
        },
        {
          onSuccess: () => {
            triggerToast(
              'success',
              t('commentTemplate.dialog.messages.updateSuccess')
            )
            onOpenChange(false)
          },
          onError: handleApiError,
        }
      )
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          triggerToast(
            'success',
            t('commentTemplate.dialog.messages.createSuccess')
          )
          onOpenChange(false)
        },
        onError: handleApiError,
      })
    }
  }

  const isSaving = useMemo(
    () => createMutation.isPending || updateMutation.isPending,
    [createMutation.isPending, updateMutation.isPending]
  )

  const handleModeChange = useCallback(
    (checked: boolean) => {
      const newMode = checked ? EditorMode.MARKDOWN : EditorMode.PLAIN
      const currentContent = control._formValues.content || initialEditorValue

      if (newMode === EditorMode.MARKDOWN) {
        setEditorMode(EditorMode.MARKDOWN)
      } else {
        setIsSwitchingMode(true)
        try {
          const markdownText = serializeToMarkdown(currentContent)
          const parsedNodes = parseMarkdownToSlate(markdownText)
          setValue('content', parsedNodes)
          setEditorMode(EditorMode.PLAIN)
        } catch {
          setEditorMode(EditorMode.MARKDOWN)
        } finally {
          setIsSwitchingMode(false)
        }
      }
    },
    [control, setValue]
  )

  const getSubmitButtonText = () => {
    if (isSaving) {
      return t('common.saving')
    }
    if (isEditMode) {
      return t('common.save')
    }
    return t('common.add')
  }

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen)
  }

  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Allow hotkeys (Cmd/Ctrl + key)
    if (e.metaKey || e.ctrlKey) return

    const target = e.target
    if (
      target instanceof HTMLElement &&
      target.getAttribute('role') === 'textbox'
    ) {
      e.stopPropagation()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[640px] bg-white shadow-xl rounded-xl p-6">
          <DialogHeader className="space-y-2">
            <DialogTitle className="m-0 text-lg font-semibold text-gray-light-900">
              {isEditMode
                ? t('commentTemplate.dialog.editTitle')
                : t('commentTemplate.dialog.createTitle')}
            </DialogTitle>
          </DialogHeader>

          <form
            id="comment-template-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <div className="space-y-1">
              <label htmlFor="name" className="text-sm font-normal">
                {t('commentTemplate.fields.name')}
              </label>
              <Input
                id="name"
                className="w-full"
                placeholder={t('commentTemplate.dialog.namePlaceholder')}
                disabled={isSaving}
                {...register('name', {
                  required: t('commentTemplate.dialog.messages.nameRequired'),
                  validate: (value) => {
                    if (!value.trim()) {
                      return t('commentTemplate.dialog.messages.nameRequired')
                    }
                    return true
                  },
                })}
                maxLength={MAX_NAME_LENGTH}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-normal">
                {t('commentTemplate.fields.content')}
              </label>
              <Controller
                name="content"
                control={control}
                rules={{
                  required: t(
                    'commentTemplate.dialog.messages.contentRequired'
                  ),
                  validate: (value) => {
                    const markdown = serializeToMarkdown(value)
                    if (!markdown.trim()) {
                      return t(
                        'commentTemplate.dialog.messages.contentRequired'
                      )
                    }
                    return true
                  },
                }}
                render={({ field }) => (
                  <div
                    className="border rounded-md h-60 border-gray-light-200"
                    onKeyDownCapture={handleKeyDownCapture}
                  >
                    <RichTextEditor
                      key={`editor-${open ? 'open' : 'closed'}-${template?.id || 'new'}`}
                      value={field.value || initialEditorValue}
                      onChange={field.onChange}
                      maxCharacters={MAX_CONTENT_LENGTH}
                      disabled={isSaving || isSwitchingMode}
                      placeholder={t(
                        'commentTemplate.dialog.contentPlaceholder'
                      )}
                      showHorizontalScroll
                      mode={editorMode}
                    />
                  </div>
                )}
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.content.message}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="show-markdown-template"
                checked={editorMode === EditorMode.MARKDOWN}
                onCheckedChange={handleModeChange}
                disabled={isSaving || isSwitchingMode}
              />
              <label
                htmlFor="show-markdown-template"
                className="text-sm font-normal text-gray-700 cursor-pointer select-none"
              >
                {t('tflViewer.addCommentDialog.showMarkdown')}
              </label>
            </div>
          </form>

          <DialogFooter className="flex gap-3">
            <Button
              type="button"
              variant="outlined"
              color="default"
              className="flex-1 h-10 text-sm font-semibold"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="comment-template-form"
              variant="solid"
              color="primary"
              className="flex-1 h-10 text-sm font-semibold"
              disabled={isSaving || !isValid}
            >
              {getSubmitButtonText()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
